import { grey } from '@mui/material/colors';
import { FC } from 'react';
import { BaseEdge, type EdgeProps, getBezierPath } from 'reactflow';

import {
  LabwareDisplayDataByTaskId,
  selectEdgeHasSameInstrumentsById,
  selectLabwareColorIndexById,
  selectLabwareDisplayDataByTaskId,
} from '../../../features/selectors';
import { useAppSelector } from '../../../hooks';
import { getColorFromIndex, parseHandlePairId } from '../../../utils';
import {
  DESELECTED_EDGE_STROKE_WIDTH,
  SELECTED_EDGE_STROKE_WIDTH,
} from '../constants';

export function getLabwareEdgeColor(args: {
  labwareDisplayDataByTaskId: LabwareDisplayDataByTaskId;
  colorIndexById: Record<string, number>;
  source: string;
  sourceHandleId?: string | null;
}): string {
  const { source, sourceHandleId, labwareDisplayDataByTaskId, colorIndexById } =
    args;

  const labwareSourceColorIndex = colorIndexById[source];
  if (labwareSourceColorIndex !== undefined) {
    // edge is immediately connected to source labware node
    return getColorFromIndex(labwareSourceColorIndex);
  }

  if (sourceHandleId != null) {
    const handlePairId = parseHandlePairId(sourceHandleId);
    if (handlePairId !== null) {
      const labwareDisplayData =
        labwareDisplayDataByTaskId[source]?.[handlePairId];
      if (labwareDisplayData != null) {
        // edge is indirectly downstream of a labware node
        return getColorFromIndex(labwareDisplayData.colorIndex);
      }
    }
  }

  // no upstream labware node
  return grey[600];
}

function useLabwareEdgeColor(args: {
  source: string;
  sourceHandleId?: string | null;
}): string {
  const { source, sourceHandleId } = args;
  const labwareDisplayDataByTaskId = useAppSelector(
    selectLabwareDisplayDataByTaskId
  );
  const colorIndexById = useAppSelector(selectLabwareColorIndexById);

  return getLabwareEdgeColor({
    labwareDisplayDataByTaskId,
    colorIndexById,
    source,
    sourceHandleId,
  });
}

const DASH_SPACING = 8;

export const WFBEdge: FC<EdgeProps> = (props) => {
  const {
    sourceX,
    sourceY,
    targetX,
    targetY,
    selected,
    source,
    sourceHandleId,
  } = props;

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  const color = useLabwareEdgeColor({
    source,
    sourceHandleId,
  });

  const dashed = useAppSelector(selectEdgeHasSameInstrumentsById)[props.id];

  return (
    <>
      <BaseEdge
        path={edgePath}
        style={{
          stroke: color,
          strokeWidth: selected
            ? SELECTED_EDGE_STROKE_WIDTH
            : DESELECTED_EDGE_STROKE_WIDTH,
          strokeDasharray: dashed ? DASH_SPACING : undefined,
          strokeLinecap: 'round',
        }}
      />
    </>
  );
};
